import { api, apiRequest, buildWcUrl, getPaginationFromHeaders, PaginationHeaders } from './api';
import { Product, ProductQueryParams, ProductVariation } from '../types/woocommerce';
import { isProductFrench, getFrenchFilterParams } from '../config/french-filter';

// Fonction utilitaire pour filtrer les produits "Non classé"
const filterOutUncategorized = (products: Product[]): Product[] => {
  return products.filter(product => 
    !product.categories.some(cat => 
      cat.slug === 'non-classe' || cat.name.toLowerCase() === 'non classé'
    )
  );
};

export interface ProductsResponse {
  products: Product[];
  pagination: PaginationHeaders;
}

export interface VariationsResponse {
  variations: ProductVariation[];
  pagination: PaginationHeaders;
}

// Service pour les produits
export const productsService = {
  // Récupérer tous les produits avec pagination
  async getProducts(params?: ProductQueryParams): Promise<ProductsResponse> {
    // Ajouter les paramètres de filtre français
    const frenchParams = getFrenchFilterParams();
    const mergedParams = { ...frenchParams, ...params };
    
    const response = await api.get<Product[]>(buildWcUrl('products'), { params: mergedParams });
    
    // Filtrer les produits
    let filteredProducts = response.data.filter(isProductFrench);
    filteredProducts = filterOutUncategorized(filteredProducts);
    
    return {
      products: filteredProducts,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer un produit par ID
  async getProduct(id: number): Promise<Product> {
    return apiRequest.get<Product>(buildWcUrl(`products/${id}`));
  },

  // Récupérer un produit par slug
  async getProductBySlug(slug: string): Promise<Product> {
    const products = await apiRequest.get<Product[]>(buildWcUrl('products'), {
      params: { slug },
    });
    if (products.length === 0) {
      throw new Error('Produit non trouvé');
    }
    return products[0];
  },

  // Rechercher des produits
  async searchProducts(search: string, params?: ProductQueryParams): Promise<ProductsResponse> {
    const frenchParams = getFrenchFilterParams();
    const mergedParams = { ...frenchParams, ...params, search };
    
    const response = await api.get<Product[]>(buildWcUrl('products'), {
      params: mergedParams,
    });
    
    // Filtrer les produits
    let filteredProducts = response.data.filter(isProductFrench);
    filteredProducts = filterOutUncategorized(filteredProducts);
    
    return {
      products: filteredProducts,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les produits en promotion
  async getOnSaleProducts(params?: ProductQueryParams): Promise<ProductsResponse> {
    const frenchParams = getFrenchFilterParams();
    const mergedParams = { ...frenchParams, ...params, on_sale: true };
    
    const response = await api.get<Product[]>(buildWcUrl('products'), {
      params: mergedParams,
    });
    
    // Filtrer les produits
    let filteredProducts = response.data.filter(isProductFrench);
    filteredProducts = filterOutUncategorized(filteredProducts);
    
    return {
      products: filteredProducts,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les produits vedettes
  async getFeaturedProducts(params?: ProductQueryParams): Promise<ProductsResponse> {
    const frenchParams = getFrenchFilterParams();
    const mergedParams = { ...frenchParams, ...params, featured: true };
    
    const response = await api.get<Product[]>(buildWcUrl('products'), {
      params: mergedParams,
    });
    
    // Filtrer les produits
    let filteredProducts = response.data.filter(isProductFrench);
    filteredProducts = filterOutUncategorized(filteredProducts);
    
    return {
      products: filteredProducts,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les produits par catégorie
  async getProductsByCategory(categoryId: number, params?: ProductQueryParams): Promise<ProductsResponse> {
    const response = await api.get<Product[]>(buildWcUrl('products'), {
      params: { ...params, category: categoryId.toString() },
    });
    
    // Filtrer les produits
    const filteredProducts = filterOutUncategorized(response.data);
    
    return {
      products: filteredProducts,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les produits par tag
  async getProductsByTag(tagId: number, params?: ProductQueryParams): Promise<ProductsResponse> {
    const response = await api.get<Product[]>(buildWcUrl('products'), {
      params: { ...params, tag: tagId.toString() },
    });
    return {
      products: response.data,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer les produits liés
  async getRelatedProducts(productId: number): Promise<Product[]> {
    const product = await this.getProduct(productId);
    if (product.related_ids.length === 0) {
      return [];
    }
    
    return apiRequest.get<Product[]>(buildWcUrl('products'), {
      params: { include: product.related_ids.join(',') },
    });
  },

  // Récupérer les variations d'un produit variable
  async getProductVariations(productId: number, params?: ProductQueryParams): Promise<VariationsResponse> {
    const response = await api.get<ProductVariation[]>(
      buildWcUrl(`products/${productId}/variations`),
      { params }
    );
    return {
      variations: response.data,
      pagination: getPaginationFromHeaders(response.headers),
    };
  },

  // Récupérer une variation spécifique
  async getProductVariation(productId: number, variationId: number): Promise<ProductVariation> {
    return apiRequest.get<ProductVariation>(
      buildWcUrl(`products/${productId}/variations/${variationId}`)
    );
  },

  // Récupérer les produits recommandés (upsells)
  async getUpsellProducts(productId: number): Promise<Product[]> {
    const product = await this.getProduct(productId);
    if (product.upsell_ids.length === 0) {
      return [];
    }
    
    return apiRequest.get<Product[]>(buildWcUrl('products'), {
      params: { include: product.upsell_ids.join(',') },
    });
  },

  // Récupérer les produits croisés (cross-sells)
  async getCrossSellProducts(productId: number): Promise<Product[]> {
    const product = await this.getProduct(productId);
    if (product.cross_sell_ids.length === 0) {
      return [];
    }
    
    return apiRequest.get<Product[]>(buildWcUrl('products'), {
      params: { include: product.cross_sell_ids.join(',') },
    });
  },

  // Vérifier la disponibilité du stock
  isInStock(product: Product | ProductVariation): boolean {
    if (!product.manage_stock) {
      return product.stock_status === 'instock';
    }
    return product.stock_quantity !== null && product.stock_quantity > 0;
  },

  // Obtenir le prix formaté
  getFormattedPrice(price: string | number, currency: string = 'EUR'): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(numPrice);
  },

  // Obtenir le pourcentage de réduction
  getDiscountPercentage(regularPrice: string, salePrice: string): number {
    const regular = parseFloat(regularPrice);
    const sale = parseFloat(salePrice);
    if (regular === 0 || sale === 0 || sale >= regular) {
      return 0;
    }
    return Math.round(((regular - sale) / regular) * 100);
  },
};
